'use client';

import { createContext, useEffect, useMemo, useState } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';

import { convertArrayToObject } from 'src/helpers/utils';
import { fetchLoggedInUserDetails } from 'src/services/auth';
import type { PropertyManagerSettingItem, UserDetails } from 'src/types/auth';

export type ErrorHandler = (
	route: string | undefined,
	error: unknown,
) => unknown;

export type AuthContextValue = [
	UserDetails | null,
	Dispatch<SetStateAction<UserDetails | null>>,
	boolean,
];

export const AuthContext = createContext<AuthContextValue>([
	null,
	() => {},
	true,
]);

interface AuthProviderProps {
	onAuthError?: ErrorHandler;
	children: ReactNode;
}

export const AuthProvider = ({ children, onAuthError }: AuthProviderProps) => {
	const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
	const [isProfileFetching, setIsProfileFetching] = useState(true);

	useEffect(() => {
		setIsProfileFetching(true);
		fetchLoggedInUserDetails({ onError: onAuthError })
			.then((res) => {
				if (!res?.data) {
					return;
				}

				setUserDetails({
					...res.data,
					propertyManagerSettingsObj:
						convertArrayToObject<PropertyManagerSettingItem>(
							res.data?.settings,
							'slug',
						),
				});
			})
			.catch(() => {})
			.finally(() => setIsProfileFetching(false));
	}, [onAuthError]);

	const authValues = useMemo<AuthContextValue>(
		() => [userDetails, setUserDetails, isProfileFetching] as const,
		[isProfileFetching, userDetails],
	);

	return (
		<AuthContext.Provider value={authValues}>{children}</AuthContext.Provider>
	);
};
